// Production Configuration - Secure and Optimized
// Focus: Content-first approach with performance optimization

export const siteConfig = {
    // Global settings
    global: {
        companyName: 'Digital Insights Network',
        tagline: 'Exploring Innovation Across Industries',
        footerNotice: 'This domain may be available for transfer to the right steward.',
        disclaimer: 'This is an independent website not affiliated with any individuals or companies mentioned.',
        inquiryEmail: 'domains@letsgetweird.com',
        lambdaUrl: process.env.VITE_LAMBDA_URL || process.env.LAMBDA_URL || 'https://gwlhbd2sfwsinole45bi2dmbom0ozksy.lambda-url.us-east-1.on.aws/',
        analyticsEnabled: true,
        cookieConsentRequired: true
    },
    
    // Performance settings
    performance: {
        lazyLoadImages: true,
        preloadCriticalAssets: true,
        enableServiceWorker: true,
        cacheStrategy: 'network-first'
    },
    
    // Security settings
    security: {
        enableCSP: true,
        enableHSTS: true,
        blockFraming: true,
        sanitizeUserInput: true
    }
};

// Domain-specific content configuration
export const domainContent = {
    // Tech Leaders - Industry Focus
    'jensenhuang.com': {
        theme: 'tech-innovation',
        title: 'GPU Computing & AI Innovation Hub',
        subtitle: 'Exploring the Future of Accelerated Computing',
        primaryContent: {
            hero: 'Discover insights on GPU technology, AI advancement, and the future of computing.',
            sections: [
                {
                    title: 'AI & Deep Learning',
                    content: 'Resources and insights on artificial intelligence and neural networks.',
                    icon: 'ai'
                },
                {
                    title: 'GPU Technology',
                    content: 'Understanding graphics processing and parallel computing.',
                    icon: 'gpu'
                },
                {
                    title: 'Innovation Insights',
                    content: 'Trends shaping the future of technology.',
                    icon: 'innovation'
                }
            ]
        },
        seo: {
            keywords: ['GPU computing', 'AI innovation', 'deep learning', 'technology insights'],
            description: 'Explore GPU computing, AI innovation, and technology insights on this independent platform.'
        },
        disclaimer: 'This website provides technology insights and is not affiliated with Jensen Huang or NVIDIA Corporation.',
        subtleAvailability: true
    },
    
    'chrismalachowsky.com': {
        theme: 'engineering-innovation',
        title: 'Engineering Excellence Resource Center',
        subtitle: 'Bridging Hardware and Software Innovation',
        primaryContent: {
            hero: 'Exploring the intersection of computer engineering and system design.',
            sections: [
                {
                    title: 'System Architecture',
                    content: 'Deep dives into modern computing architectures.',
                    icon: 'architecture'
                },
                {
                    title: 'Engineering Principles',
                    content: 'Foundational concepts in computer engineering.',
                    icon: 'engineering'
                },
                {
                    title: 'Innovation Frameworks',
                    content: 'Methodologies for technical innovation.',
                    icon: 'framework'
                }
            ]
        },
        seo: {
            keywords: ['engineering excellence', 'system architecture', 'computer engineering', 'innovation'],
            description: 'Engineering excellence resources and system architecture insights on this independent platform.'
        },
        disclaimer: 'This is an educational resource not affiliated with Chris Malachowsky or any associated entities.',
        subtleAvailability: true
    },
    
    'curtispriem.com': {
        theme: 'visual-computing',
        title: 'Visual Computing Innovation Portal',
        subtitle: 'The Art and Science of Graphics Technology',
        primaryContent: {
            hero: 'Exploring visual computing, graphics technology, and creative applications.',
            sections: [
                {
                    title: 'Graphics Innovation',
                    content: 'The evolution and future of visual computing.',
                    icon: 'graphics'
                },
                {
                    title: 'Creative Technology',
                    content: 'Where art meets computational power.',
                    icon: 'creative'
                },
                {
                    title: 'Industry Insights',
                    content: 'Trends in graphics and visualization.',
                    icon: 'trends'
                }
            ]
        },
        seo: {
            keywords: ['visual computing', 'graphics technology', 'creative technology', 'industry insights'],
            description: 'Visual computing and graphics technology insights on this independent educational platform.'
        },
        disclaimer: 'Independent educational site not affiliated with Curtis Priem or related organizations.',
        subtleAvailability: true
    },
    
    'richardsocher.com': {
        theme: 'nlp-ai',
        title: 'Natural Language AI Research Hub',
        subtitle: 'Advancing Language Understanding Through AI',
        primaryContent: {
            hero: 'Exploring natural language processing, deep learning, and AI research.',
            sections: [
                {
                    title: 'NLP Breakthroughs',
                    content: 'Latest advances in natural language understanding.',
                    icon: 'nlp'
                },
                {
                    title: 'Deep Learning',
                    content: 'Neural network architectures for language.',
                    icon: 'neural'
                },
                {
                    title: 'AI Applications',
                    content: 'Practical uses of language AI technology.',
                    icon: 'applications'
                }
            ]
        },
        seo: {
            keywords: ['natural language processing', 'NLP', 'AI research', 'deep learning'],
            description: 'Natural language processing and AI research insights on this independent platform.'
        },
        disclaimer: 'Educational platform not affiliated with Richard Socher or any associated companies.',
        subtleAvailability: true
    },
    
    'deepindergoyal.com': {
        theme: 'foodtech-innovation',
        title: 'Food Tech Innovation Insights',
        subtitle: 'The Digital Transformation of Dining',
        primaryContent: {
            hero: 'Exploring how technology is revolutionizing the food service industry.',
            sections: [
                {
                    title: 'Digital Dining',
                    content: 'The future of food delivery and restaurant tech.',
                    icon: 'dining'
                },
                {
                    title: 'Market Insights',
                    content: 'Trends shaping the food service sector.',
                    icon: 'market'
                },
                {
                    title: 'Innovation Stories',
                    content: 'Case studies in food tech transformation.',
                    icon: 'stories'
                }
            ]
        },
        seo: {
            keywords: ['food tech', 'digital dining', 'restaurant technology', 'food service innovation'],
            description: 'Food technology and digital dining insights on this independent industry platform.'
        },
        disclaimer: 'Industry insights platform not affiliated with Deepinder Goyal or Zomato.',
        subtleAvailability: true
    },
    
    // Additional domains with optimized content
    'taavethinrikus.com': {
        theme: 'fintech-innovation',
        title: 'FinTech Innovation Insights',
        subtitle: 'Financial Technology & Digital Transformation',
        primaryContent: {
            hero: 'Exploring financial technology, innovation, and digital transformation in finance.',
            sections: [
                {
                    title: 'FinTech Innovation',
                    content: 'Latest developments in financial technology.',
                    icon: 'fintech'
                },
                {
                    title: 'Digital Finance',
                    content: 'Digital transformation in financial services.',
                    icon: 'digital'
                },
                {
                    title: 'Payment Systems',
                    content: 'Modern payment technologies and systems.',
                    icon: 'payments'
                }
            ]
        },
        seo: {
            keywords: ['fintech', 'financial technology', 'digital payments', 'finance innovation'],
            description: 'Financial technology and digital transformation insights on this independent platform.'
        },
        disclaimer: 'FinTech insights platform not affiliated with any individual.',
        subtleAvailability: true
    },
    
    'bhavishaggarwal.com': {
        theme: 'business-innovation',
        title: 'Business Innovation Hub',
        subtitle: 'Entrepreneurship & Growth Strategies',
        primaryContent: {
            hero: 'Exploring entrepreneurship, business strategy, and market innovation.',
            sections: [
                {
                    title: 'Business Strategy',
                    content: 'Strategic insights for business growth.',
                    icon: 'strategy'
                },
                {
                    title: 'Innovation',
                    content: 'Market innovation and disruption.',
                    icon: 'innovation'
                },
                {
                    title: 'Entrepreneurship',
                    content: 'Building and scaling businesses.',
                    icon: 'entrepreneurship'
                }
            ]
        },
        seo: {
            keywords: ['business innovation', 'entrepreneurship', 'growth strategies', 'market disruption'],
            description: 'Business innovation and entrepreneurship insights on this independent platform.'
        },
        disclaimer: 'Business insights platform not affiliated with any individual.',
        subtleAvailability: true
    },
    
    // Default configuration
    'default': {
        theme: 'professional',
        title: 'Professional Insights Platform',
        subtitle: 'Industry Knowledge & Innovation',
        primaryContent: {
            hero: 'Discover insights and resources for professional growth.',
            sections: [
                {
                    title: 'Industry Trends',
                    content: 'Stay informed about the latest developments.',
                    icon: 'trends'
                },
                {
                    title: 'Innovation Insights',
                    content: 'Exploring new ideas and methodologies.',
                    icon: 'innovation'
                },
                {
                    title: 'Professional Resources',
                    content: 'Tools and knowledge for success.',
                    icon: 'resources'
                }
            ]
        },
        seo: {
            keywords: ['professional insights', 'industry trends', 'innovation', 'business resources'],
            description: 'Professional insights and industry knowledge on this independent platform.'
        },
        disclaimer: 'This is an independent informational website.',
        subtleAvailability: true
    }
};

// Content modules for dynamic loading
export const contentModules = {
    techNews: {
        title: 'Technology News',
        updateFrequency: 'daily',
        sources: ['industry', 'research', 'innovation'],
        cacheTime: 3600 // 1 hour
    },
    
    businessInsights: {
        title: 'Business Strategy',
        topics: ['Digital Transformation', 'Innovation Management', 'Market Analysis'],
        updateFrequency: 'weekly'
    },
    
    industryResources: {
        title: 'Industry Resources',
        categories: ['Research Papers', 'Case Studies', 'Best Practices'],
        access: 'public'
    }
};

// Analytics configuration
export const analyticsConfig = {
    trackingEvents: {
        pageView: true,
        scrollDepth: true,
        timeOnPage: true,
        clicks: true,
        formInteractions: true,
        errors: true
    },
    
    customEvents: {
        highIntent: ['contact_form_submit', 'deep_scroll', 'long_session'],
        conversionGoals: ['form_submission', 'domain_inquiry'],
        engagement: ['tab_interaction', 'content_expansion', 'resource_download']
    },
    
    privacy: {
        anonymizeIP: true,
        respectDNT: true,
        cookieExpiry: 365 // days
    }
};

// Legal and compliance text
export const legalContent = {
    masterDisclaimer: `This website is operated independently and is not affiliated with, endorsed by, or connected to any individuals whose names may appear similar to this domain name. All content is for informational purposes only. Any similarities to actual persons or organizations are coincidental.`,
    
    privacyNotice: `We respect your privacy. This site uses cookies and analytics to improve user experience. Personal information is handled in accordance with applicable privacy laws.`,
    
    termsOfUse: `By using this website, you agree to our terms of use. Content is provided for informational purposes only and should not be considered professional advice.`,
    
    cookiePolicy: `This site uses cookies to enhance user experience and gather analytics. You can control cookie preferences through your browser settings.`
};

// Performance optimization settings
export const performanceConfig = {
    images: {
        lazy: true,
        formats: ['webp', 'jpg'],
        sizes: {
            mobile: 768,
            tablet: 1024,
            desktop: 1920
        }
    },
    
    scripts: {
        defer: true,
        async: ['analytics', 'third-party'],
        preload: ['critical']
    },
    
    cache: {
        static: 31536000, // 1 year
        dynamic: 3600, // 1 hour
        api: 300 // 5 minutes
    }
};

// Export frozen configurations for security
export default Object.freeze({
    siteConfig: Object.freeze(siteConfig),
    domainContent: Object.freeze(domainContent),
    contentModules: Object.freeze(contentModules),
    analyticsConfig: Object.freeze(analyticsConfig),
    legalContent: Object.freeze(legalContent),
    performanceConfig: Object.freeze(performanceConfig)
});